import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { EASE } from "./motion";

const inputCls = "w-full border border-[#0A1128]/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#1D4ED8] transition-colors";

export default function ApplyDialog({ job, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (job) {
      setForm({ name: "", email: "", phone: "", message: "" });
      setFile(null);
    }
  }, [job]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("job_id", job.id);
      fd.append("job_title", job.title);
      if (file) fd.append("resume", file);
      await api.post("/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Application sent for ${job.title}. We'll be in touch soon.`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {job && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-[#0A1128]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={onClose}
          data-testid="apply-dialog-overlay"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="bg-[#F4F5F7] w-full max-w-lg p-8 md:p-10 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="apply-dialog"
          >
            <div className="flex items-start justify-between mb-8">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#1D4ED8] mb-2">Applying for</p>
                <h3 className="font-display font-bold text-2xl tracking-tight text-[#0A1128]">{job.title}</h3>
                <p className="text-sm text-[#475569] mt-1">{job.company} · {job.location}</p>
              </div>
              <button onClick={onClose} className="text-[#475569] hover:text-[#0A1128]" data-testid="apply-dialog-close" aria-label="Close">
                <X size={22} />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="apply-name" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="apply-email" />
                <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="apply-phone" />
              </div>
              <textarea rows={3} placeholder="Short note to the recruiter (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} data-testid="apply-message" />
              <label className="flex items-center justify-center gap-3 border border-dashed border-[#0A1128]/25 hover:border-[#1D4ED8] bg-white px-4 py-6 cursor-pointer transition-colors" data-testid="apply-resume-drop">
                <UploadCloud size={20} className="text-[#1D4ED8]" />
                <span className="text-sm text-[#475569]">{file ? file.name : "Attach resume (optional)"}</span>
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="apply-resume-input" />
              </label>
              <button disabled={busy} className="w-full bg-[#1D4ED8] hover:bg-[#1E3A8A] disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-4 transition-colors duration-300" data-testid="apply-submit">
                {busy ? "Sending…" : "Submit Application"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
